/**
 * Standardized Error Handling Utility
 * 
 * Provides consistent error handling across the application with:
 * - User-friendly error messages
 * - Centralized error transformation
 * - Toast notifications
 * - Error logging integration
 */

import logger from './logger';
import { showError } from './toast';
import { extractErrorMessage } from './errorExtractor';
import { ERROR_MESSAGES } from './errorMessages';

/**
 * Custom Application Error Class
 * Extends native Error with additional context
 */
export class AppError extends Error {
  constructor(message, code, statusCode, details = {}) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }
}

/**
 * Error code constants
 */
export const ERROR_CODES = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  AUTH_ERROR: 'AUTH_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  PERMISSION_ERROR: 'PERMISSION_ERROR',
  NOT_FOUND_ERROR: 'NOT_FOUND_ERROR'
};

/**
 * Re-export extractErrorMessage for backward compatibility
 * The actual implementation is in errorExtractor.js to prevent circular dependencies
 * @deprecated Import directly from './errorExtractor' instead
 */
export { extractErrorMessage } from './errorExtractor';

/**
 * Transform error to user-friendly message
 * @param {Error|AppError} error - Error object
 * @returns {string} User-friendly error message
 */
const getErrorMessage = (error) => {
  // Handle AppError instances
  if (error instanceof AppError) {
    return error.message;
  }

  // Handle axios errors
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data;

    // Extract error message using the common utility function
    // This ensures consistent error message extraction across the app
    const errorMessage = extractErrorMessage(data);
    
    switch (status) {
      case 400:
        return errorMessage || 'Invalid request. Please check your input.';
      case 401:
        return errorMessage || 'Authentication failed. Please log in again.';
      case 403:
        return errorMessage || 'You do not have permission to perform this action.';
      case 404:
        return errorMessage || 'Resource not found.';
      case 409:
        // Handle duplicate entry errors with user-friendly messages
        if (errorMessage) {
          if (errorMessage.includes('already exists') || errorMessage.includes('duplicate')) {
            if (errorMessage.includes('phone') || errorMessage.includes('wa_phone')) {
              return 'A contact with this phone number already exists.';
            } else if (errorMessage.includes('email')) {
              return 'An account with this email already exists.';
            }
          }
          return errorMessage;
        }
        return 'This resource already exists.';
      case 422:
        return errorMessage || 'Validation error. Please check your input.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
        return 'Bad gateway. The server is temporarily unavailable.';
      case 503:
        return 'Service unavailable. Please try again later.';
      default:
        return errorMessage || `Server error (${status})`;
    }
  }

  // Handle network errors
  if (error.request) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }

  // Handle timeout
  if (error.code === 'ECONNABORTED') {
    return ERROR_MESSAGES.TIMEOUT_ERROR;
  }

  // Handle specific error codes
  if (error.code === 'ERR_NETWORK') {
    return ERROR_MESSAGES.NETWORK_ERROR_DETAILED;
  }

  // Fallback - ensure we always return a string
  const fallbackMessage = error?.message;
  if (fallbackMessage && typeof fallbackMessage === 'string') {
    return fallbackMessage;
  }
  return ERROR_MESSAGES.UNEXPECTED_ERROR;
};

/**
 * Get error code from error object
 * @param {Error|AppError} error - Error object
 * @returns {string} Error code
 */
const getErrorCode = (error) => {
  if (error instanceof AppError) return error.code;
  if (error.response) {
    const status = error.response.status;
    if (status === 401) return ERROR_CODES.AUTH_ERROR;
    if (status === 403) return ERROR_CODES.PERMISSION_ERROR;
    if (status === 404) return ERROR_CODES.NOT_FOUND_ERROR;
    if (status >= 400 && status < 500) return ERROR_CODES.VALIDATION_ERROR;
    return ERROR_CODES.SERVER_ERROR;
  }
  if (error.request) return ERROR_CODES.NETWORK_ERROR;
  if (error.code === 'ECONNABORTED') return ERROR_CODES.TIMEOUT_ERROR;
  return ERROR_CODES.UNKNOWN_ERROR;
};

/**
 * Handle error with standardized processing
 * @param {Error|AppError} error - Error object
 * @param {Object} context - Error context (component, action, etc.)
 * @param {boolean} showToast - Whether to show toast notification (default: true)
 * @returns {Object} Error result object
 */
export const handleError = (error, context = {}, showToast = true) => {
  // Log error
  logger.error(
    `Error in ${context.component || 'unknown'}`,
    error,
    {
      ...context,
      errorCode: getErrorCode(error),
      statusCode: error.response?.status
    }
  );

  // Transform error to user-friendly message
  const userMessage = getErrorMessage(error);

  // Show toast notification
  if (showToast) {
    showError(userMessage);
  }

  // Return error object for component handling
  return {
    message: userMessage,
    code: getErrorCode(error),
    statusCode: error.response?.status,
    originalError: error,
    context
  };
};

/**
 * React Hook for error handling
 * @returns {Object} Error handling utilities
 */
export const useErrorHandler = () => {
  return {
    handleError: (error, context = {}, showToast = true) => 
      handleError(error, context, showToast),
    AppError,
    ERROR_CODES
  };
};

/**
 * Create a new AppError instance
 * @param {string} message - Error message
 * @param {string} code - Error code
 * @param {number} statusCode - HTTP status code
 * @param {Object} details - Additional error details
 * @returns {AppError} AppError instance
 */
export const createError = (message, code = ERROR_CODES.UNKNOWN_ERROR, statusCode = null, details = {}) => {
  return new AppError(message, code, statusCode, details);
};

export default {
  handleError,
  useErrorHandler,
  AppError,
  ERROR_CODES,
  createError,
  extractErrorMessage
};

